import React from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Send,
  ArrowUpRight,
  Globe2,
} from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function ContactSection() {
  return (
    <section className="relative overflow-hidden bg-[#0B0B12] py-16 md:py-24">
      {/* ===== Premium background ===== */}
      <div className="pointer-events-none absolute inset-0">
        {/* dark base */}
        <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_20%,rgba(244,124,32,0.18),transparent_60%),radial-gradient(1000px_circle_at_80%_20%,rgba(232,62,111,0.16),transparent_60%),radial-gradient(1000px_circle_at_60%_90%,rgba(201,162,39,0.10),transparent_55%)]" />
        {/* vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_35%,rgba(0,0,0,0.70)_100%)]" />
        {/* subtle noise */}
        <div
          className="absolute inset-0 opacity-[0.05] mix-blend-soft-light"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)' opacity='.25'/%3E%3C/svg%3E\")",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* LEFT: info */}
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="lg:col-span-6"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 px-4 py-2 backdrop-blur">
              <Globe2 size={16} className="text-[#F47C20]" />
              <span className="text-xs font-semibold tracking-[0.22em] uppercase text-white/70">
                Contact
              </span>
            </div>

            <h2 className="mt-6 text-4xl md:text-5xl font-extrabold leading-tight text-white">
              Get In{" "}
              <span className="bg-gradient-to-r from-[#F47C20] to-[#E83E6F] bg-clip-text text-transparent">
                Touch
              </span>
            </h2>

            <p className="mt-4 text-base md:text-lg text-white/70 leading-relaxed max-w-xl">
              Have a question or need help? Reach out and let’s chat. Our team
              responds quickly for export and manufacturing inquiries.
            </p>

            {/* Contact blocks */}
            <div className="mt-10 space-y-5">
              <InfoCard
                icon={<MapPin size={18} />}
                title="India Office"
                lines={[
                  "Plot No. 1, Near To Saint Martin Academy,",
                  "Jagat Khera Road, Kalli Paschim,",
                  "Lucknow, Uttar Pradesh - 226301, India.",
                ]}
              />

              <InfoCard
                icon={<MapPin size={18} />}
                title="UK Office"
                lines={[
                  "Amera Imports Private Limited, Unit 11d",
                  "Cosgrove Way, Luton, England LU1 1xl.",
                ]}
              />

              <InfoCard
                icon={<MapPin size={18} />}
                title="USA Office"
                lines={[
                  "Amera Imports LLC",
                  "1209 Mountain Road Pl NE",
                  "Ste R Albuquerque, NM, USA 87110.",
                ]}
              />

              <InfoCard
                icon={<Phone size={18} />}
                title="Call / WhatsApp"
                lines={[
                  "+91-6387961502 (Call - India)",
                  "+91-9506484022 (WhatsApp - India)",
                  "+44 7770 599304 (Call - UK Office)",
                  "+1 (505) 5056791 (Call - USA)",
                ]}
              />

              <InfoCard
                icon={<Mail size={18} />}
                title="Email"
                lines={[
                  "pragya.sales1@ameraexports.com",
                  "riya.sales2@ameraexports.com",
                  "rishika.seo.sales3@ameraexports.com",
                ]}
              />
            </div>
          </motion.div>

          {/* RIGHT: Form */}
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="lg:col-span-6"
          >
            <div className="relative">
              {/* glow behind card */}
              <div className="pointer-events-none absolute -inset-8 rounded-[28px] bg-gradient-to-r from-[#F47C20]/20 to-[#E83E6F]/20 blur-3xl opacity-60" />

              <div className="relative rounded-[26px] border border-white/12 bg-white/6 backdrop-blur-xl shadow-[0_22px_80px_rgba(0,0,0,0.45)] overflow-hidden">
                {/* top accent */}
                <div className="h-[4px] w-full bg-gradient-to-r from-[#F47C20] via-[#C9A227] to-[#E83E6F]" />

                <div className="p-7 md:p-10">
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <p className="text-xs font-semibold tracking-[0.2em] uppercase text-white/60">
                        Send a Message
                      </p>
                      <h3 className="mt-2 text-2xl md:text-3xl font-extrabold text-white">
                        Let’s build something great
                      </h3>
                      <p className="mt-2 text-sm md:text-base text-white/65">
                        Share your requirement and we’ll get back within 24 hours.
                      </p>
                    </div>

                    <div className="hidden sm:flex h-12 w-12 rounded-2xl bg-gradient-to-r from-[#F47C20] to-[#E83E6F] items-center justify-center text-white shadow-lg shadow-black/20">
                      <Send size={20} />
                    </div>
                  </div>

                  <form className="mt-8 space-y-4">
                    <Field label="Name">
                      <input
                        type="text"
                        placeholder="Your full name"
                        className={inputClass}
                      />
                    </Field>

                    <Field label="Email">
                      <input
                        type="email"
                        placeholder="you@email.com"
                        className={inputClass}
                      />
                    </Field>

                    <Field label="Message">
                      <textarea
                        rows={5}
                        placeholder="Tell us what you need…"
                        className={inputClass + " resize-none"}
                      />
                    </Field>

                    <div className="pt-2 flex flex-col sm:flex-row gap-3">
                      <motion.button
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        type="button"
                        className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold text-white
                                   bg-gradient-to-r from-[#F47C20] to-[#E83E6F]
                                   shadow-lg shadow-black/20 hover:opacity-95 transition"
                      >
                        Submit
                        <ArrowUpRight size={18} />
                      </motion.button>

                      <p className="text-xs text-white/55 sm:ml-auto sm:text-right sm:self-center">
                        By submitting, you agree to be contacted by Amera Exports.
                      </p>
                    </div>
                  </form>
                </div>
              </div>

              {/* small decorative corner dot */}
              <div className="pointer-events-none absolute -right-3 -top-3 h-20 w-20 rounded-full bg-[#E83E6F]/25 blur-2xl" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------- UI helpers ---------- */

function InfoCard({
  icon,
  title,
  lines,
}: {
  icon: React.ReactNode;
  title: string;
  lines: string[];
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md px-6 py-5 transition hover:bg-white/[0.06]">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-[#F47C20] to-[#E83E6F] p-[1px] shrink-0">
          <div className="h-full w-full rounded-xl bg-[#0B0B12] flex items-center justify-center text-white/85">
            {icon}
          </div>
        </div>

        {/* Text */}
        <div className="text-left">
          <p className="text-sm font-semibold tracking-wide text-white/90">
            {title}
          </p>

          <div className="mt-2 space-y-1 text-sm text-white/70 leading-6 font-normal">
            {lines.map((l) => (
              <p key={l}>{l}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold tracking-wide text-white/70">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputClass =
  "w-full rounded-lg border border-white/10 bg-white/[0.05] px-4 py-3 text-sm font-normal text-white/85 placeholder:text-white/40 " +
  "outline-none transition duration-200 focus:border-[#F47C20]/60 focus:ring-2 focus:ring-[#F47C20]/20";